import React, { useState } from 'react';
import { Play, CheckCircle, XCircle, ChevronDown, ChevronUp, FlaskConical } from 'lucide-react';

const SAMPLE_DATA = {
  lead: { firstName: 'John', lastName: 'Doe', email: 'john@example.com', phone: '+15550142', score: 87, industry: 'SaaS', company: 'TechCorp' },
  deal: { id: 'deal_123', stage: 'Proposal', value: 5000, contact: 'john@example.com' },
  call: { id: 'call_456', outcome: 'interested', duration: 342, notes: 'Wants demo next week' },
};

const WorkflowTestPanel = ({ nodes }) => {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [sampleType, setSampleType] = useState('lead');
  const [expandedResult, setExpandedResult] = useState(null);

  const runTest = async () => {
    if (!nodes?.length) return;
    setRunning(true);
    setResults([]);

    for (let i = 0; i < nodes?.length; i++) {
      await new Promise(r => setTimeout(r, 600 + Math.random() * 400));
      const success = Math.random() > 0.1;
      setResults(prev => [...prev, {
        id: `r${i}`,
        step: i + 1,
        node: nodes?.[i]?.label,
        status: success ? 'success' : 'error',
        duration: `${Math.floor(100 + Math.random() * 300)}ms`,
        output: success
          ? `Step completed successfully. Output: { status: 'ok', id: 'exec_${Math.random()?.toString(36)?.substr(2, 6)}' }`
          : `Error: Connection timeout after 3 retries`,
      }]);
    }
    setRunning(false);
  };

  const samplePayload = SAMPLE_DATA?.[sampleType];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
        <FlaskConical className="w-5 h-5 text-blue-600" />
        <h3 className="text-sm font-semibold text-gray-900">Workflow Test Environment</h3>
      </div>
      <div className="p-4 space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Sample Data Type</label>
          <div className="flex gap-2">
            {Object.keys(SAMPLE_DATA)?.map(type => (
              <button
                key={type}
                onClick={() => setSampleType(type)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                  sampleType === type ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Sample Payload</label>
          <pre className="p-3 bg-gray-900 rounded-lg text-xs text-green-400 font-mono overflow-x-auto">
            {JSON.stringify(samplePayload, null, 2)}
          </pre>
        </div>

        <button
          onClick={runTest}
          disabled={running || !nodes?.length}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {running ? (
            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Running simulation...</>
          ) : (
            <><Play className="w-4 h-4" />Run Test Simulation</>
          )}
        </button>

        {results?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-700">Execution Results</p>
            {results?.map(result => (
              <div key={result?.id} className={`border rounded-lg overflow-hidden ${
                result?.status === 'success' ? 'border-green-200' : 'border-red-200'
              }`}>
                <div
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer ${
                    result?.status === 'success' ? 'bg-green-50' : 'bg-red-50'
                  }`}
                  onClick={() => setExpandedResult(expandedResult === result?.id ? null : result?.id)}
                >
                  <div className="flex items-center gap-2">
                    {result?.status === 'success'
                      ? <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                      : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                    <span className="text-xs font-medium text-gray-800">Step {result?.step}: {result?.node}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{result?.duration}</span>
                    {expandedResult === result?.id ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                  </div>
                </div>
                {expandedResult === result?.id && (
                  <div className="px-3 py-2 bg-gray-900">
                    <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{result?.output}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowTestPanel;
